import React, { InputHTMLAttributes, forwardRef } from 'react';
import { clsx } from 'clsx';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  containerClassName?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  helperText,
  leftIcon,
  rightIcon,
  containerClassName,
  className,
  id,
  ...props
}, ref) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
  
  const inputClasses = clsx(
    'form-input',
    {
      'border-red-300 dark:border-red-600 focus:ring-red-500 focus:border-red-500': error,
      'pl-10 sm:pl-12': leftIcon,
      'pr-10 sm:pr-12': rightIcon,
    },
    className
  );
  
  const containerClasses = clsx(
    'form-group',
    containerClassName
  );

  // Ensure icons are properly sized
  const iconClasses = "w-4 h-4 sm:w-5 sm:h-5 text-gray-400 dark:text-gray-500";

  return (
    <div className={containerClasses}>
      {label && (
        <label
          htmlFor={inputId}
          className="form-label"
        >
          {label}
        </label>
      )}
      
      <div className="relative">
        {leftIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
            <div className={iconClasses}>
              {leftIcon}
            </div>
          </div>
        )}
        
        <input
          ref={ref}
          id={inputId}
          className={inputClasses}
          {...props}
        />
        
        {rightIcon && (
          <div className="absolute inset-y-0 right-0 pr-3 sm:pr-4 flex items-center">
            <div className={iconClasses}>
              {rightIcon}
            </div>
          </div>
        )}
      </div>
      
      {error && (
        <p className="mt-1 text-xs sm:text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
      
      {helperText && !error && (
        <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
          {helperText}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
